// پیش‌نمایش Client فایل داده مرجع پیش از بارگذاری (SCR-CONSOLE-008): همان قالب سرور (C-AGENT-160) — CSV با سرآیند و ستون
// الزامی `key`، JSON آرایه اشیاء با `key`. اعتبارسنجی نهایی با سرور (PHX-PLT-0502 و خطاهای ردیف).

export type ReferenceFormat = 'csv' | 'json';

export interface ReferencePreview {
  format: ReferenceFormat;
  columns: string[];
  rows: Record<string, string>[];
  total: number;
  /** کلید i18n مشکل ساختاری (بدون ستون key، JSON نامعتبر). */
  problemKey?: string;
}

const PREVIEW_ROWS = 5;

export function detectFormat(fileName: string): ReferenceFormat | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.csv')) {
    return 'csv';
  }
  if (lower.endsWith('.json')) {
    return 'json';
  }
  return null;
}

/** تجزیه CSV با پشتیبانی نقل‌قول دوتایی (RFC 4180). */
export function parseCsv(content: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const text = content.replace(/^\uFEFF/, '');
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') {
        i += 1;
      }
      row.push(cell);
      cell = '';
      if (row.some((value) => value !== '')) {
        rows.push(row);
      }
      row = [];
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some((value) => value !== '')) {
    rows.push(row);
  }
  return rows;
}

export function previewReferenceFile(content: string, format: ReferenceFormat): ReferencePreview {
  if (format === 'csv') {
    const [header = [], ...data] = parseCsv(content);
    const columns = header.map((column) => column.trim());
    const rows = data
      .slice(0, PREVIEW_ROWS)
      .map((cells) => Object.fromEntries(columns.map((column, index) => [column, cells[index] ?? ''])));
    return {
      format,
      columns,
      rows,
      total: data.length,
      problemKey: columns.includes('key') ? undefined : 'console:reference_data.upload.problem_no_key',
    };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return {
      format,
      columns: [],
      rows: [],
      total: 0,
      problemKey: 'console:reference_data.upload.problem_json',
    };
  }
  if (!Array.isArray(parsed)) {
    return {
      format,
      columns: [],
      rows: [],
      total: 0,
      problemKey: 'console:reference_data.upload.problem_json',
    };
  }
  const objects = parsed.filter(
    (item): item is Record<string, unknown> => !!item && typeof item === 'object',
  );
  const columns = [...new Set(objects.slice(0, PREVIEW_ROWS).flatMap((item) => Object.keys(item)))];
  const rows = objects.slice(0, PREVIEW_ROWS).map((item) =>
    Object.fromEntries(
      columns.map((column) => {
        const value = item[column];
        return [column, value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value)];
      }),
    ),
  );
  return {
    format,
    columns,
    rows,
    total: parsed.length,
    problemKey: objects.every((item) => typeof item.key === 'string' || typeof item.key === 'number')
      ? undefined
      : 'console:reference_data.upload.problem_no_key',
  };
}
